import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Drawer,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Assignment as AssignmentIcon,
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import useSWR from 'swr';
import { fetcher } from '../../utils/api';
import { FeedbackDetails } from './FeedbackDetails';
import { useAuth } from '../../contexts/AuthContext';

interface FeedbackItem {
  id: string;
  type: 'BUG' | 'SUGGESTION';
  title: string;
  severity?: 'P0' | 'P1' | 'P2' | 'P3';
  category?: string;
  status: 'UNRESOLVED' | 'TRIAGED' | 'IN_PROGRESS' | 'DONE' | 'WONTFIX' | 'DUPLICATE' | 'NEEDS_INFO';
  assigned_to?: string;
  assigned_email?: string;
  reporter_email: string;
  created_at: string;
  attachment_count: number;
  comment_count: number;
  business_id?: string | null;
  business_name?: string | null;
}

interface FeedbackFilters {
  status?: string;
  type?: string;
  severity?: string;
  category?: string;
  assigned_to?: string;
  search?: string;
  created_from?: Date | null;
  created_to?: Date | null;
}

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  'UNRESOLVED': 'error',
  'TRIAGED': 'warning',
  'IN_PROGRESS': 'info',
  'DONE': 'success',
  'WONTFIX': 'default',
  'DUPLICATE': 'default',
  'NEEDS_INFO': 'warning'
};

const severityColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  'P0': 'error',
  'P1': 'warning',
  'P2': 'info',
  'P3': 'default'
};

export const FeedbackList: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filters, setFilters] = useState<FeedbackFilters>({
    // Default to All (no status filter)
    status: ''
  });
  const [selectedFeedback, setSelectedFeedback] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { isPlatformDev } = useAuth();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Build query parameters
  const queryParams = new URLSearchParams();
  queryParams.set('page', (page + 1).toString());
  queryParams.set('limit', rowsPerPage.toString());
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value && key !== 'created_from' && key !== 'created_to') {
      queryParams.set(key, value);
    }
  });
  
  if (debouncedSearch) queryParams.set('search', debouncedSearch);
  if (filters.created_from) queryParams.set('created_from', filters.created_from.toISOString());
  if (filters.created_to) queryParams.set('created_to', filters.created_to.toISOString());

  const { data, error, isLoading, mutate } = useSWR(
    `/admin/beta-feedback?${queryParams.toString()}`,
    fetcher,
    { refreshInterval: 30000 } // Refresh every 30 seconds
  );

  // Get unresolved count for badge
  const { data: unresolvedData } = useSWR(
    '/admin/beta-feedback?status=UNRESOLVED&limit=1',
    fetcher,
    { refreshInterval: 30000 }
  );

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleFilterChange = useCallback((key: keyof FeedbackFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0); // Reset to first page when filtering
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ status: '' });
    setSearchTerm('');
    setPage(0);
  }, []);

  const handleRowClick = useCallback((feedbackId: string) => {
    setSelectedFeedback(feedbackId);
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedFeedback(null);
    // Refresh the list when drawer closes (in case of updates)
    mutate();
  }, [mutate]);

  const bulkAssignToMe = useCallback(() => {
    // TODO: Implement bulk operations
    console.log('Bulk assign to me');
  }, []);

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load feedback: {error.message}
      </Alert>
    );
  }

  const feedbackItems: FeedbackItem[] = data?.results || [];
  const total = data?.total || 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" component="h1">
              Developer Portal
            </Typography>
            {unresolvedData?.total > 0 && (
              <Badge badgeContent={unresolvedData.total} color="error">
                <Chip label="Unresolved" color="error" size="small" />
              </Badge>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={() => mutate()} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={bulkAssignToMe}
              disabled={feedbackItems.length === 0}
            >
              Assign to Me
            </Button>
          </Stack>
        </Box>

        {/* Filters Panel */}
        {showFilters && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    label="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      endAdornment: <SearchIcon />
                    }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status || ''}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="UNRESOLVED">Unresolved</MenuItem>
                      <MenuItem value="TRIAGED">Triaged</MenuItem>
                      <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                      <MenuItem value="DONE">Done</MenuItem>
                      <MenuItem value="WONTFIX">Won't Fix</MenuItem>
                      <MenuItem value="DUPLICATE">Duplicate</MenuItem>
                      <MenuItem value="NEEDS_INFO">Needs Info</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filters.type || ''}
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="BUG">Bug</MenuItem>
                      <MenuItem value="SUGGESTION">Suggestion</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Severity</InputLabel>
                    <Select
                      value={filters.severity || ''}
                      onChange={(e) => handleFilterChange('severity', e.target.value)}
                      label="Severity"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="P0">P0 (Critical)</MenuItem>
                      <MenuItem value="P1">P1 (High)</MenuItem>
                      <MenuItem value="P2">P2 (Medium)</MenuItem>
                      <MenuItem value="P3">P3 (Low)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <DatePicker
                    label="From Date"
                    value={filters.created_from}
                    onChange={(date) => handleFilterChange('created_from', date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 1 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Results Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned</TableCell>
                  <TableCell>Reporter</TableCell>
                  {isPlatformDev && <TableCell>Business</TableCell>}
                  <TableCell>Created</TableCell>
                  <TableCell>Attachments</TableCell>
                  <TableCell>Comments</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={isPlatformDev ? 12 : 11} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : feedbackItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isPlatformDev ? 12 : 11} align="center">
                      <Typography color="textSecondary">
                        No feedback items found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  feedbackItems.map((item) => (
                    <TableRow 
                      key={item.id} 
                      hover 
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(item.id)}
                    >
                      <TableCell>
                        <Chip 
                          label={item.type} 
                          color={item.type === 'BUG' ? 'error' : 'info'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={item.title}>
                          <Typography noWrap sx={{ maxWidth: 200 }}>
                            {item.title}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {item.severity && (
                          <Chip 
                            label={item.severity} 
                            color={severityColors[item.severity]} 
                            size="small" 
                          />
                        )}
                      </TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>
                        <Chip 
                          label={item.status.replace('_', ' ')} 
                          color={statusColors[item.status]} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>{item.assigned_email || '-'}</TableCell>
                      <TableCell>{item.reporter_email}</TableCell>
                      {isPlatformDev && (
                        <TableCell>{item.business_name || item.business_id || '-'}</TableCell>
                      )}
                      <TableCell>
                        {new Date(item.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        {item.attachment_count > 0 && (
                          <Badge badgeContent={item.attachment_count} color="primary">
                            <Typography variant="body2">📎</Typography>
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {item.comment_count > 0 && (
                          <Badge badgeContent={item.comment_count} color="primary">
                            <Typography variant="body2">💬</Typography>
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(item.id);
                        }}>
                          <OpenIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        {/* Details Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleCloseDrawer}
          sx={{
            '& .MuiDrawer-paper': {
              width: { xs: '100%', sm: '80%', md: '60%', lg: '50%' },
              maxWidth: 800
            }
          }}
        >
          {selectedFeedback && (
            <FeedbackDetails 
              feedbackId={selectedFeedback} 
              onClose={handleCloseDrawer}
              onUpdate={() => mutate()}
            />
          )}
        </Drawer>
      </Box>
    </LocalizationProvider>
  );
}; 